import moment from 'moment';

/**
 * Check if a day at the given offset is a weekend
 */
export const isWeekend = (startDate: moment.Moment, offset: number): boolean => {
    return [6, 0].includes(moment(startDate).add(offset, 'days').locale('en').weekday());
};

/**
 * Snap a day index to the nearest weekday
 */
export const snapToNearestWeekday = (
    dayIndex: number,
    startDate: moment.Moment,
    length: number
): number => {
    // If it's already a weekday, return as-is
    if (!isWeekend(startDate, dayIndex)) {
        return dayIndex;
    }

    // Look for the nearest weekday
    let leftWeekday = -1;
    let rightWeekday = -1;

    // Search left for the nearest weekday
    for (let i = dayIndex - 1; i >= 0; i--) {
        if (!isWeekend(startDate, i)) {
            leftWeekday = i;
            break;
        }
    }

    // Search right for the nearest weekday
    for (let i = dayIndex + 1; i < length; i++) {
        if (!isWeekend(startDate, i)) {
            rightWeekday = i;
            break;
        }
    }

    // Return the nearest weekday, preferring the left side if equal distance
    if (leftWeekday >= 0 && rightWeekday >= 0) {
        const leftDistance = dayIndex - leftWeekday;
        const rightDistance = rightWeekday - dayIndex;
        return leftDistance <= rightDistance ? leftWeekday : rightWeekday;
    } else if (leftWeekday >= 0) {
        return leftWeekday;
    } else if (rightWeekday >= 0) {
        return rightWeekday;
    }

    // Fallback: return the original index if no weekdays found
    return dayIndex;
};

/**
 * Get the flex length for a day (0.15 for weekend, 1 for weekday)
 */
export const adjustedDayLength = (startDate: moment.Moment, offset: number): number => {
    return isWeekend(startDate, offset) ? 0.15 : 1;
};

/**
 * Calculate total flex length for the entire calendar period
 */
export const getTotalFlexLength = (startDate: moment.Moment, length: number): number => {
    let total = 0;
    for (let i = 0; i < length; i++) {
        total += adjustedDayLength(startDate, i);
    }
    return total;
};

/**
 * Get the day index from a mouse X position, always snapping to weekdays
 */
export const getDayIndexFromPosition = (
    x: number,
    containerRef: React.RefObject<HTMLDivElement>,
    startDate: moment.Moment,
    length: number
): number => {
    if (!containerRef.current) return 0;

    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = Math.max(0, Math.min(x - rect.left, rect.width));
    const totalFlexLength = getTotalFlexLength(startDate, length);

    let currentPosition = 0;
    let rawDayIndex = 0;
    for (let i = 0; i < length; i++) {
        const dayWidth = (adjustedDayLength(startDate, i) / totalFlexLength) * rect.width;
        if (relativeX <= currentPosition + dayWidth) {
            rawDayIndex = i;
            break;
        }
        currentPosition += dayWidth;
        if (i === length - 1) {
            rawDayIndex = length - 1;
        }
    }

    // Always snap to the nearest weekday
    return snapToNearestWeekday(rawDayIndex, startDate, length);
};

/**
 * Calculate the position and width style for a calendar item
 */
export const getItemStyle = (
    startDay: number,
    endDay: number,
    startDate: moment.Moment,
    length: number
): { left: string; width: string } => {
    const totalFlexLength = getTotalFlexLength(startDate, length);

    let leftPosition = 0;
    for (let i = 0; i < startDay; i++) {
        leftPosition += (adjustedDayLength(startDate, i) / totalFlexLength) * 100;
    }

    let width = 0;
    for (let i = startDay; i <= endDay; i++) {
        width += (adjustedDayLength(startDate, i) / totalFlexLength) * 100;
    }

    return { left: `${leftPosition}%`, width: `${width}%` };
};

/**
 * Calculate the position and width style for a drag overlay that may extend beyond the visible calendar
 * This function can handle negative start days and end days beyond the calendar length
 * The overlay will be clipped to the calendar area but show the correct visual extent
 */
export const getDragOverlayStyle = (
    startDay: number,
    endDay: number,
    startDate: moment.Moment,
    length: number
): { left: string; width: string } => {
    const totalFlexLength = getTotalFlexLength(startDate, length);

    // Calculate the theoretical left position (can be negative)
    let theoreticalLeft = 0;
    if (startDay < 0) {
        // Calculate position for days before the visible calendar
        for (let i = startDay; i < 0; i++) {
            theoreticalLeft -= (adjustedDayLength(startDate, i) / totalFlexLength) * 100;
        }
    } else {
        // Standard calculation for visible days
        for (let i = 0; i < startDay; i++) {
            theoreticalLeft += (adjustedDayLength(startDate, i) / totalFlexLength) * 100;
        }
    }

    // Calculate total theoretical width (including out-of-bounds portions)
    let totalWidth = 0;
    for (let i = startDay; i <= endDay; i++) {
        totalWidth += (adjustedDayLength(startDate, i) / totalFlexLength) * 100;
    }

    // Clamp the left position to not go before the calendar starts
    const clampedLeft = Math.max(0, theoreticalLeft);

    // Adjust width to account for any clipped left portion
    const leftClipAmount = Math.max(0, -theoreticalLeft);
    const adjustedWidth = totalWidth - leftClipAmount;

    // Ensure width doesn't exceed the calendar bounds
    const maxWidth = 100 - clampedLeft;
    const finalWidth = Math.min(adjustedWidth, maxWidth);

    return { left: `${clampedLeft}%`, width: `${finalWidth}%` };
};

/**
 * Calculate the start and end day indices for a calendar item
 */
export const getItemDayIndices = (
    itemStartDate: moment.Moment,
    itemEndDate: moment.Moment | undefined,
    calendarStartDate: moment.Moment,
    calendarLength: number
): { startDay: number; endDay: number } => {
    const startDay = Math.max(0, itemStartDate.diff(calendarStartDate, 'days'));
    const endDay = Math.min(
        calendarLength - 1,
        (itemEndDate || itemStartDate).diff(calendarStartDate, 'days')
    );

    return { startDay, endDay };
};

/**
 * Calculate the actual (unclamped) start and end day indices for a calendar item
 * These can be negative or beyond the calendar length for items that extend beyond the visible range
 */
export const getActualItemDayIndices = (
    itemStartDate: moment.Moment,
    itemEndDate: moment.Moment | undefined,
    calendarStartDate: moment.Moment
): { actualStartDay: number; actualEndDay: number } => {
    const actualStartDay = itemStartDate.diff(calendarStartDate, 'days');
    const actualEndDay = (itemEndDate || itemStartDate).diff(calendarStartDate, 'days');

    return { actualStartDay, actualEndDay };
};

/**
 * Check if a calendar item is visible within the current date range
 */
export const isItemVisible = (
    itemStartDate: moment.Moment,
    itemEndDate: moment.Moment | undefined,
    calendarStartDate: moment.Moment,
    calendarLength: number
): boolean => {
    const calendarEndDate = moment(calendarStartDate).add(calendarLength, 'days');
    return itemStartDate.isBefore(calendarEndDate) &&
        (itemEndDate || itemStartDate).isSameOrAfter(calendarStartDate);
};

/**
 * Get the border radius class based on item position within calendar view
 */
export const getItemBorderClass = (
    itemStartDate: moment.Moment,
    itemEndDate: moment.Moment | undefined,
    calendarStartDate: moment.Moment,
    calendarLength: number,
    classes: {
        calendar_item_start_end: string;
        calendar_item_start: string;
        calendar_item_end: string;
    }
): string => {
    const calendarEndDate = moment(calendarStartDate).add(calendarLength, 'days');
    const startInRange = itemStartDate.isSameOrAfter(calendarStartDate);
    const endInRange = (itemEndDate || itemStartDate).isSameOrBefore(calendarEndDate);

    if (startInRange && endInRange) {
        return classes.calendar_item_start_end;
    } else if (startInRange) {
        return classes.calendar_item_start;
    } else if (endInRange) {
        return classes.calendar_item_end;
    }
    return '';
};

/**
 * Count the number of weekdays in [0, dayIndex) — i.e. the weekday ordinal of a calendar day.
 * For example, if the calendar starts on Monday:
 *   weekdayOrdinal(Mon, 0) = 0, weekdayOrdinal(Mon, 4) = 4, weekdayOrdinal(Mon, 7) = 5
 * Handles negative day indices for items before the calendar start.
 */
export const weekdayOrdinal = (startDate: moment.Moment, dayIndex: number): number => {
    let count = 0;
    if (dayIndex > 0) {
        for (let i = 0; i < dayIndex; i++) {
            if (!isWeekend(startDate, i)) count++;
        }
    } else if (dayIndex < 0) {
        for (let i = -1; i >= dayIndex; i--) {
            if (!isWeekend(startDate, i)) count--;
        }
    }
    return count;
};

/**
 * Given a weekday ordinal, return the calendar day index of that weekday.
 * Inverse of weekdayOrdinal: calendarDayFromWeekdayOrdinal(startDate, weekdayOrdinal(startDate, d)) === d
 * (when d is a weekday)
 */
export const calendarDayFromWeekdayOrdinal = (startDate: moment.Moment, ordinal: number): number => {
    if (ordinal >= 0) {
        let day = 0;
        let counted = 0;
        for (let safety = 0; safety < 1000; safety++) {
            if (!isWeekend(startDate, day)) {
                if (counted === ordinal) return day;
                counted++;
            }
            day++;
        }
    } else {
        let day = -1;
        let counted = -1;
        for (let safety = 0; safety < 1000; safety++) {
            if (!isWeekend(startDate, day)) {
                if (counted === ordinal) return day;
                counted--;
            }
            day--;
        }
    }
    return 0; // fallback
};

/**
 * Count the number of weekdays (Mon-Fri) between two dates (inclusive)
 */
export const countWeekdaysInclusive = (start: moment.Moment, end: moment.Moment): number => {
    let count = 0;
    let current = start.clone();
    while (current.isSameOrBefore(end, 'day')) {
        const day = current.day();
        if (day !== 0 && day !== 6) count++;
        current.add(1, 'day');
    }
    return count;
};

/**
 * Given a start date and a number of weekdays, find the end date (inclusive) that covers that many weekdays
 */
export const addWeekdays = (start: moment.Moment, weekdayCount: number): moment.Moment => {
    let count = 0;
    let current = start.clone();
    while (count < weekdayCount) {
        const day = current.day();
        if (day !== 0 && day !== 6) count++;
        if (count < weekdayCount) current.add(1, 'day');
    }
    return current;
};

/**
 * Calculate calendar item display properties including position, styling, and visibility
 */
export const getCalendarItemDisplayProps = (
    itemStartDate: moment.Moment,
    itemEndDate: moment.Moment | undefined,
    calendarStartDate: moment.Moment,
    calendarLength: number,
    classes: {
        calendar_item_start_end: string;
        calendar_item_start: string;
        calendar_item_end: string;
    }
) => {
    // Check if item is visible
    const isVisible = isItemVisible(itemStartDate, itemEndDate, calendarStartDate, calendarLength);

    if (!isVisible) {
        return { isVisible: false as const };
    }

    // Calculate position
    const { startDay, endDay } = getItemDayIndices(itemStartDate, itemEndDate, calendarStartDate, calendarLength);
    const itemStyle = getItemStyle(startDay, endDay, calendarStartDate, calendarLength);
    const borderStyle = getItemBorderClass(itemStartDate, itemEndDate, calendarStartDate, calendarLength, classes);

    return {
        isVisible: true as const,
        startDay,
        endDay,
        itemStyle,
        borderStyle,
        itemLength: parseFloat(itemStyle.width)
    };
}; 